/**
 * ActionBadges — shared visual primitives for action category / pillar /
 * urgency / lead-time. Single set of components used by both ActionCenter
 * and DoneActions so the visual language of an action card stays
 * consistent across surfaces.
 *
 * Consumers: pages/ActionCenter.jsx, pages/DoneActions.jsx.
 * Source of styling truth: utils/actionVisuals.js.
 */
import { Clock } from 'lucide-react'
import Tooltip from './Tooltip.jsx'
import {
  CATEGORY_META, PILLAR_COLORS, URGENCY_COLORS,
  URGENCY_REASON_COPY, LEAD_TIME_TIER_CLASS,
} from '../../utils/actionVisuals.js'
import { leadTimeHours, formatLeadTime, leadTimeTier } from '../../utils/leadTime.js'


export function CategoryIcon({ category, size = 14 }) {
  const meta = CATEGORY_META[category]
  if (!meta) return null
  const Icon = meta.icon
  return (
    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${meta.bg} border ${meta.border}`}>
      <Icon size={size} className={meta.color} />
    </span>
  )
}


export function PillarBadge({ pillar }) {
  const c = PILLAR_COLORS[pillar] ?? { text: 'text-slate-400', bg: 'bg-white/5', border: 'border-white/10' }
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${c.text} ${c.bg} border ${c.border}`}>
      {pillar}
    </span>
  )
}


export function UrgencyBadge({ urgency, reason, wos, leadTimeHours: leadHours }) {
  const c = URGENCY_COLORS[urgency] ?? { text: 'text-slate-400', bg: 'bg-white/5', border: 'border-white/10' }
  const isImmediate = urgency?.startsWith('Immediate')
  const baseCopy    = URGENCY_REASON_COPY[reason] ?? null
  // Build a contextual tooltip — augments the canned copy with the actual numbers.
  const tip = (() => {
    if (!reason) return urgency
    let extra = ''
    if (reason === 'low_wos' && typeof wos === 'number') {
      extra = ` (WOS = ${wos.toFixed(1)} wks).`
    }
    if (typeof leadHours === 'number' && leadHours >= 24 && isImmediate) {
      extra += ` Event itself is ${Math.round(leadHours)}h out — the urgency reflects when you must act, not when the event happens.`
    }
    return `${urgency} — ${baseCopy ?? ''}${extra}`.trim()
  })()
  const badge = (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${c.text} ${c.bg} border ${c.border}`}>
      {isImmediate && <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />}
      {isImmediate ? '24h' : '48–96h'}
    </span>
  )
  return baseCopy ? <Tooltip content={tip} placement="top">{badge}</Tooltip> : badge
}


export function LeadTimeBadge({ eventAt, now }) {
  const hours = leadTimeHours(eventAt, now)
  const tier  = leadTimeTier(hours)
  const label = formatLeadTime(hours)
  const cls   = LEAD_TIME_TIER_CLASS[tier] ?? LEAD_TIME_TIER_CLASS.unknown
  return (
    <Tooltip
      content={
        eventAt
          ? `Event at ${new Date(eventAt).toLocaleString()} — refreshed every 10 min`
          : 'No scheduled event time available'
      }
      placement="top"
    >
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tabular-nums border cursor-help ${cls}`}>
        <Clock size={9} />
        {label}
      </span>
    </Tooltip>
  )
}
